import { useLocation, Navigate } from 'react-router-dom';
import { MARKETING_URL, isMarketingPath } from '../config/appUrl';
import { OWNER_HOST, OWNER_PORTAL_URL, isOwnerPortalPath } from '../utils/portalHost';

const APP_HOST = 'app.hostea.io';
const MARKETING_HOSTS = ['hostea.io', 'www.hostea.io'];

/**
 * Splits traffic between the marketing site (hostea.io), the product app
 * (app.hostea.io) and the owner portal (owner.hostea.io). Acts as the safety
 * net for product routes that vercel.json 301s cannot match safely (dynamic
 * catch-alls like /:slug, /:company/guestbook/...).
 * Inactive on localhost and Vercel preview deployments.
 */
const DomainGuard = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const { hostname } = window.location;

  // hostea.io serving a product path -> hand over to app.hostea.io
  if (MARKETING_HOSTS.includes(hostname) && !isMarketingPath(pathname)) {
    window.location.replace(`https://${APP_HOST}${pathname}${search}${hash}`);
    return null; // avoid flashing product content during the redirect
  }

  if (hostname === OWNER_HOST) {
    // Short canonical paths live here: /:slug = login, /:slug/home|activity|...
    // (the router also aliases legacy /:slug/owner/* by stripping the segment).
    // Root has no slug to route to, and known APP surfaces don't belong on this
    // host — bounce both to the app. Everything else is owner-portal routing.
    const appOnlyPath = /^\/(dashboard|login|signup|onboarding|create-hotel-setup|oauth-success|set-password|checkin|guestbook|guestbook-redirect|apartment|taxi|chatbot|service|staff|admin|integrations|payment|error|unauthorized)(\/|$)/.test(pathname);
    if (pathname === '/') {
      // PWA start / bare domain: route a known owner to their portal
      // (owner-manifest has no start_url — iOS opens the added page — but the
      // installed app's storage starts empty, so this is the warm-start path)
      try {
        const stored = JSON.parse(localStorage.getItem('owner-data') || 'null');
        const slug = stored?.companySlug || stored?.company?.slug;
        if (slug) return <Navigate to={`/${slug}/home`} replace />;
      } catch (e) { /* fall through */ }
      window.location.replace(`https://${APP_HOST}`);
      return null;
    }
    if (appOnlyPath) {
      window.location.replace(`https://${APP_HOST}${pathname}${search}${hash}`);
      return null;
    }
    return children;
  }

  if (hostname === APP_HOST) {
    // Rollout step 2 (env set): owner-portal paths move to the dedicated host.
    // Never remove this redirect — old email links rely on it indefinitely.
    if (OWNER_PORTAL_URL && isOwnerPortalPath(pathname)) {
      window.location.replace(`${OWNER_PORTAL_URL}${pathname}${search}${hash}`);
      return null;
    }
    // Product root -> login (which already forwards authenticated users to /dashboard)
    if (pathname === '/') return <Navigate to="/login" replace />;
    // Marketing path requested on the app subdomain -> send back to hostea.io
    if (isMarketingPath(pathname)) {
      window.location.replace(`${MARKETING_URL}${pathname}${search}${hash}`);
      return null;
    }
  }

  return children;
};

export default DomainGuard;
